import React, {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, MenuItem, Modal, Paper, TextField} from '@mui/material';
import {PublicService} from '../../services/public.service';
import {InstitutionService} from "../../services/institution.service";
import {validateCodeFormat} from './institutionCode';
import { useTranslation } from 'react-i18next';
import './CreateInstitution.css';

interface CreateInstitutionProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    onSuccess?: (message?: string, email?: string) => void;
    selectedPlan?: string;
}

const NOT_SURE_YET = 'Not sure yet';

// Keep names in sync with the landing PricingSection plan cards.
const PLAN_OPTIONS = [
    { name: NOT_SURE_YET, labelKey: 'create.plans.notSure' },
    { name: 'Free', labelKey: 'create.plans.free' },
    { name: 'Core HR', labelKey: 'create.plans.coreHr' },
    { name: 'Complete', labelKey: 'create.plans.complete' },
    { name: 'Enterprise', labelKey: 'create.plans.enterprise' },
] as const;

const CreateInstitution: React.FC<CreateInstitutionProps> = ({
    isModal = false,
    open = false,
    onClose,
    onSuccess,
    selectedPlan
}) => {
    const { t } = useTranslation(['institutions', 'common']);
    const navigate = useNavigate();
    const [institution, setInstitution] = useState({ name: '', code: '', description: '', email: '' });
    const [plan, setPlan] = useState(selectedPlan || NOT_SURE_YET);

    useEffect(() => {
        if (open) setPlan(selectedPlan || NOT_SURE_YET);
    }, [open, selectedPlan]);
    const [error, setError] = useState('');
    const [codeError, setCodeError] = useState('');
    const [codeStatus, setCodeStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [setupCreating, setSetupCreating] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isCodeValid = codeStatus === 'available';

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const resetForm = () => {
        setInstitution({ name: '', code: '', description: '', email: '' });
        setError('');
        setCodeError('');
        setCodeStatus('idle');
        setSuccessMessage('');
    };

    const runAvailabilityCheck = async (code: string) => {
        try {
            const isUnique = isModal
                ? await PublicService.checkCodeUniqueness(code)
                : await InstitutionService.checkCodeUniqueness(code);
            setCodeStatus(isUnique ? 'available' : 'taken');
            setCodeError(isUnique ? '' : t('create.codeTaken'));
        } catch (err) {
            console.error('Error checking code uniqueness:', err);
            setCodeStatus('idle');
            setCodeError(t('create.codeCheckFailed'));
        }
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const code = e.target.value.toLowerCase();
        setInstitution((prev) => ({ ...prev, code }));

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (code === '') {
            setCodeError('');
            setCodeStatus('idle');
            return;
        }

        const formatError = validateCodeFormat(code);
        if (formatError) {
            setCodeError(formatError);
            setCodeStatus('idle');
            return;
        }

        setCodeError('');
        setCodeStatus('checking');
        debounceRef.current = setTimeout(() => runAvailabilityCheck(code), 350);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setIsLoading(true);
        setSetupCreating(true);

        try {
            if (isModal) {
                // Public mode - create institution without authentication
                const chosen = PLAN_OPTIONS.find((p) => p.name === plan);
                const payload = chosen && chosen.name !== NOT_SURE_YET
                    // Read by the operator who approves the institution: always in English.
                    ? { ...institution, description: `${institution.description}\n\nRequested plan: ${t(chosen.labelKey, { lng: 'en' })}` }
                    : institution;
                await PublicService.createInstitution(payload);
                const message = t('create.publicSuccess');
                setSuccessMessage(message);
                resetForm();
                if (onSuccess) onSuccess(message, institution.email);
            } else {
                // Authenticated mode
                await InstitutionService.createInstitution(institution);
                setSuccessMessage(t('create.success'));
                navigate('/institutions');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || t('create.createFailed'));
            console.error('Error creating institution:', err);
        } finally {
            setIsLoading(false);
            setSetupCreating(false);
        }
    };

    const content = (
        <Box className="ci-form" sx={{ maxWidth: 600, margin: '0 auto' }}>
            <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 2, boxShadow: 'none' }}>
                <div className="ci-rule" />
                <h2 className="ci-title">{t('create.title')}</h2>
                <p className="ci-lede">
                    {t('create.lede')}
                </p>
                {error && <div className="ci-error" role="alert">{error}</div>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label={t('create.nameLabel')}
                        value={institution.name}
                        onChange={(e) => setInstitution({ ...institution, name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label={t('create.codeLabel')}
                        value={institution.code}
                        onChange={handleCodeChange}
                        margin="normal"
                        required
                        error={codeStatus === 'taken' || (!!codeError && codeStatus === 'idle')}
                        helperText={
                            codeStatus === 'available'
                                ? <Box component="span" className="ci-code-ok">{t('create.codeAvailable')}</Box>
                                : codeStatus === 'checking'
                                    ? <Box component="span" className="ci-code-checking">{t('create.codeChecking')}</Box>
                                    : codeError
                        }
                    />
                    <TextField
                        fullWidth
                        label={t('create.descriptionLabel')}
                        value={institution.description}
                        onChange={(e) => setInstitution({ ...institution, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                        required
                    />
                    {isModal && (
                        <TextField
                            fullWidth
                            select
                            label={t('create.planLabel')}
                            value={plan}
                            onChange={(e) => setPlan(e.target.value)}
                            margin="normal"
                            helperText={t('create.planHelp')}
                        >
                            {PLAN_OPTIONS.map((option) => (
                                <MenuItem key={option.name} value={option.name}>
                                    {t(option.labelKey)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        fullWidth
                        label={t('create.emailLabel')}
                        value={institution.email}
                        onChange={(e) => setInstitution({ ...institution, email: e.target.value })}
                        margin="normal"
                        type="email"
                        required
                        helperText={t('create.emailHelp')}
                    />

                    {setupCreating && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                            {t('create.creatingSetup')}
                        </Alert>
                    )}

                    <div className="ci-actions">
                        <button
                            type="button"
                            className="ci-btn ci-btn-quiet"
                            onClick={() => {
                                if (isModal && onClose) {
                                    resetForm();
                                    onClose();
                                } else {
                                    navigate('/institutions');
                                }
                            }}
                            disabled={isLoading}
                        >
                            {t('common:actions.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="ci-btn ci-btn-primary"
                            disabled={!institution.name || !institution.code || !institution.description || !institution.email || !isCodeValid || isLoading}
                        >
                            {isLoading ? t('create.creating') : t('create.submit')}
                        </button>
                    </div>
                </form>
            </Paper>
        </Box>
    );

    if (isModal) {
        const modalStyle = {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: '0 24px 60px rgba(15, 31, 26, 0.28)',
            borderRadius: 2,
            outline: 'none'
        };

        return (
            <Modal
                open={open}
                onClose={() => {
                    if (onClose) {
                        resetForm();
                        onClose();
                    }
                }}
            >
                <Box sx={modalStyle}>
                    {content}
                </Box>
            </Modal>
        );
    }

    // Regular page view
    return (
        <div style={{ padding: '20px' }}>
            {content}
        </div>
    );
};

export default CreateInstitution;
